import { faCircleQuestion, faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@/components/ui/button';
import { useTranslator } from '@/hooks/useTranslator';
import { useIntercomConsentBanner } from './hooks/useIntercomConsentBanner';

/**
 * App-level cookie-consent prompt for Intercom chat support.
 *
 * A lightweight, dismissible banner — not a blocking modal — so it never stalls
 * onboarding. Shown once to any signed-in user who has not yet decided (never
 * to an impersonated session — see `useIntercomConsentBanner`); Accept
 * boots Intercom, Decline (or the ✕) records the choice. Either way the banner
 * hides and, thanks to the persisted decided/declined state, never returns.
 *
 * This is the primary entry point for consent. The "?" help button
 * (`AppHeaderHelpButton`) remains a secondary one — someone who declined here
 * can still open the dialog from there later.
 */
export function IntercomConsentBanner() {
  const { getTranslation: t } = useTranslator({ page: 'appHeader' });
  const { visible, accept, decline } = useIntercomConsentBanner();

  if (!visible) return null;

  return (
    // Bottom-RIGHT, not bottom-left. Two reasons:
    //
    // The left corner is taken. The sidebar rail runs full height with its
    // expand/collapse chevron pinned at the bottom, and the expanded panel
    // shares this `z-50` — so a card at `left-4` covers the chevron and is in
    // turn covered by the panel. `IntercomLauncherDismiss` learned the same
    // lesson: the comment in `intercom.ts` says left placement is no escape.
    //
    // And the right corner is where consent leads — after Accept the Intercom
    // launcher boots into exactly this spot, so the prompt appears where the
    // chat bubble will be. Nothing competes for it beforehand: the launcher and
    // its dismiss ✕ are both gated on consent already being granted.
    //
    // `bottom-4`, the true corner. An earlier `bottom-24` was meant to clear the
    // toaster (also bottom-right) but landed the card over the dashboard's
    // active-tasks panel, covering real content to dodge a transient one.
    <div className='fixed right-4 bottom-4 z-50 w-[calc(100%-2rem)] max-w-sm rounded-lg border border-border bg-card p-4 shadow-lg'>
      <div className='flex items-start gap-3'>
        <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10'>
          <FontAwesomeIcon
            icon={faCircleQuestion}
            className='text-sm text-primary'
          />
        </span>
        <div className='min-w-0 flex-1'>
          <p className='text-sm font-medium text-foreground'>
            {t('intercomConsent.title')}
          </p>
          <p className='mt-1 text-xs text-muted-foreground'>
            {t('intercomConsent.description')}{' '}
            <a
              href='https://www.retraced.com/pages/privacy-policy'
              target='_blank'
              rel='noreferrer'
              className='text-primary underline'
            >
              {t('intercomConsent.privacyPolicy')}
            </a>
          </p>
        </div>
        <Button
          variant='ghost'
          size='icon'
          aria-label={t('intercomConsent.dismiss')}
          onClick={decline}
          className='-mt-1 -mr-1 shrink-0'
        >
          <FontAwesomeIcon icon={faXmark} />
        </Button>
      </div>
      <div className='mt-3 flex justify-end gap-2'>
        <Button variant='outline' onClick={decline}>
          {t('intercomConsent.decline')}
        </Button>
        <Button variant='default' onClick={accept}>
          {t('intercomConsent.accept')}
        </Button>
      </div>
    </div>
  );
}
